/**
 * Request and submission validation. A submission failing any check never reaches the
 * reducer. Checks, in order (cheap and explanatory first, so a late phone hears "late"
 * rather than "malformed"):
 *   6 still open, 1 before `deadlineAt` (server arrival), 2 submitter in `targets`,
 *   5 one per participant unless `allowRevision`, 3+4 value matches spec and size caps.
 * Only kinds a shipped game needs are built. An unsupported kind is dropped at emit
 * (`supportedKind` in `applyEffects`) and refused again here for requests opened by an
 * older build. What an answer *means* is the game's, via `onSubmission`.
 */
// ---------------------------------------------------------------------------
// Narrowing a request to one viewer
// ---------------------------------------------------------------------------
/** Is this a per-participant map of specs rather than a single one? */
function isPerParticipant(spec) {
    return !('kind' in spec);
}
/** The participant id an actor or audience carries, or null for a host / display. */
export function participantOf(who) {
    return 'participantId' in who ? who.participantId : null;
}
/**
 * The spec this viewer is shown. A per-participant record narrows to one spec, since a
 * renderer keyed by `kind` cannot narrow a map. Null when the record has no entry for
 * this viewer: a game bug, which the caller reports.
 */
export function specFor(request, who) {
    const { spec } = request;
    if (!isPerParticipant(spec))
        return spec;
    const id = participantOf(who);
    return id === null ? null : (spec[id] ?? null);
}
/** The same narrowing for `prompt`, which may also be per-participant. */
export function promptFor(prompt, who) {
    if (typeof prompt === 'string')
        return prompt;
    const id = participantOf(who);
    return (id === null ? undefined : prompt[id]) ?? '';
}
// ---------------------------------------------------------------------------
// Check 2: is this actor being asked?
// ---------------------------------------------------------------------------
/**
 * Does `targets` include this actor? `onRoster` comes from the roster, not the socket's
 * claim. A display with `canDrive` *is* the host for asks as well as actions, so a
 * host-targeted spec must be safe for a room to see (no answer in a verdict `subject`);
 * `packages/server/test/redaction.test.ts` audits `snapshot.requests` for that.
 */
export function targetsInclude(targets, who, onRoster, displayCanDrive = false) {
    if (who.kind === 'host')
        return targets === 'host';
    if (who.kind === 'display')
        return targets === 'host' && displayCanDrive;
    if (who.kind === 'system')
        return false;
    if (typeof targets === 'object')
        return targets.participantIds.includes(who.participantId);
    return targets === 'allPlayers' && onRoster;
}
const bad = (error) => ({ ok: false, error });
/**
 * Hard ceiling on any text, independent of the spec's `maxLength`, so a spec that forgot
 * it cannot let a malicious client post megabytes.
 */
const TEXT_CEILING = 4096;
/** How many entries a `gradeList` or a multi-pick may carry. */
const LIST_CEILING = 256;
/** Which kinds this build can validate. `applyEffects` drops anything else at emit time. */
export function supportedKind(kind) {
    return (kind === 'none' ||
        kind === 'press' ||
        kind === 'text' ||
        kind === 'number' ||
        kind === 'wager' ||
        kind === 'choice' ||
        kind === 'pickParticipant' ||
        kind === 'verdict' ||
        kind === 'gradeList');
}
/**
 * `[min, max]` for a `choice`, both defaulting to one. Shared with the renderer so the
 * control and the validator cannot disagree about what may be collected.
 */
export function choiceRange(spec) {
    const min = spec.min ?? 1;
    return [min, Math.max(min, spec.max ?? 1)];
}
/**
 * Pick from the game's own option list (unlike `pickParticipant`, checked against the
 * roster). A single choice normalizes to a bare string, so reducers never ask whether
 * two were allowed.
 */
function validateChoice(spec, value) {
    const [min, max] = choiceRange(spec);
    const raw = Array.isArray(value) ? value : [value];
    if (raw.length > LIST_CEILING)
        return bad('That is too many options.');
    const picked = [];
    for (const one of raw) {
        if (typeof one !== 'string')
            return bad('That is not one of the options.');
        if (!spec.options.some((option) => option.id === one)) {
            // Most likely a phone holding a round that has moved on.
            return bad('That option is no longer on offer.');
        }
        if (picked.includes(one))
            return bad('Pick a different option for each slot.');
        picked.push(one);
    }
    if (picked.length < min) {
        return bad(min === 1 ? 'Pick one.' : `Pick at least ${String(min)}.`);
    }
    if (picked.length > max) {
        return bad(max === 1 ? 'Pick just one.' : `Pick no more than ${String(max)}.`);
    }
    return { ok: true, value: min === 1 && max === 1 ? picked[0] : picked };
}
function validateText(spec, value) {
    if (typeof value !== 'string')
        return bad('That answer must be text.');
    if (value.length > TEXT_CEILING)
        return bad('That answer is too long.');
    // Normalize rather than refuse: a textarea sends CRLF, a pasted single line sends \n.
    const flattened = value.replace(/\r\n?/gu, '\n');
    const text = (spec.multiline ? flattened : flattened.replace(/\n/gu, ' ')).trim();
    if (text.length > spec.maxLength)
        return bad(`Keep it under ${String(spec.maxLength)} characters.`);
    if (text.length < (spec.minLength ?? 0)) {
        return bad(spec.minLength === 1 ? 'Type an answer first.' : `That is too short.`);
    }
    // `profanityFilter` is unimplemented and nothing sets it; it is not silently honoured.
    return { ok: true, value: text };
}
/**
 * A free quantity, unlike `wager`: no score framing and decimals are allowed (a numeric
 * clue grades on exact match, which may be fractional). Range is enforced; `step` is a
 * phone-keypad hint, not a constraint core imposes.
 */
function validateNumber(spec, value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return bad('That is not a number.');
    if (value < spec.min || value > spec.max) {
        return bad(`Enter a number between ${String(spec.min)} and ${String(spec.max)}.`);
    }
    return { ok: true, value };
}
/**
 * Whole points only: `score_entries.delta` is INTEGER, so 100.5 would silently become
 * a different bet. `number` is the kind for non-point quantities.
 */
function validateWager(spec, value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return bad('That is not a number.');
    if (!Number.isInteger(value))
        return bad('Bet a whole number of points.');
    if (value < spec.min || value > spec.max) {
        return bad(`Bet between ${String(spec.min)} and ${String(spec.max)}.`);
    }
    return { ok: true, value };
}
function validatePick(spec, value, self) {
    const count = spec.count ?? 1;
    const raw = Array.isArray(value) ? value : [value];
    if (raw.length > LIST_CEILING)
        return bad('That is too many people.');
    if (raw.length !== count) {
        return bad(count === 1 ? 'Pick one person.' : `Pick ${String(count)} people.`);
    }
    const picked = [];
    for (const one of raw) {
        if (typeof one !== 'string')
            return bad('That is not a player.');
        if (!spec.candidateIds.includes(one))
            return bad('That player cannot be picked.');
        if (spec.excludeSelf && one === self)
            return bad('You cannot pick yourself.');
        if (picked.includes(one))
            return bad('Pick a different person for each slot.');
        picked.push(one);
    }
    // A single pick is a bare string, matching the shape of the ask.
    return { ok: true, value: count === 1 ? picked[0] : picked };
}
function isVerdict(value) {
    return value === 'correct' || value === 'incorrect';
}
/**
 * A whole grading pass as one answer, so a request cannot be half-answered. Keys must be
 * ids the spec listed (a stale tray cannot grade a withdrawn answer); partial is fine.
 */
function validateGradeList(spec, value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return bad('That is not a set of rulings.');
    }
    const entries = Object.entries(value);
    if (entries.length === 0)
        return bad('Rule on at least one answer.');
    if (entries.length > LIST_CEILING)
        return bad('That is too many rulings.');
    const known = new Set(spec.items.map((item) => item.id));
    const out = {};
    for (const [id, verdict] of entries) {
        if (!known.has(id))
            return bad('That answer is no longer on the list.');
        if (!isVerdict(verdict))
            return bad('A ruling is correct or incorrect.');
        out[id] = verdict;
    }
    return { ok: true, value: out };
}
/**
 * Checks 3 and 4. The returned `value` is normalized (text trimmed, single pick a bare
 * string, press `true`), so reducers see one shape.
 */
export function validateValue(spec, value, self) {
    switch (spec.kind) {
        case 'none':
            // Nothing to send; a client that sent something is on a different request.
            return bad(spec.message ?? 'There is nothing to send.');
        case 'press':
            // The answer is `receivedAt`; the value is irrelevant.
            return { ok: true, value: true };
        case 'text':
            return validateText(spec, value);
        case 'number':
            return validateNumber(spec, value);
        case 'wager':
            return validateWager(spec, value);
        case 'choice':
            return validateChoice(spec, value);
        case 'pickParticipant':
            return validatePick(spec, value, self);
        case 'verdict':
            return isVerdict(value) ? { ok: true, value } : bad('Rule correct or incorrect.');
        case 'gradeList':
            return validateGradeList(spec, value);
        default:
            // Second line of defence after `applyEffects`, for requests from a newer build.
            return bad(`This server cannot validate a ${JSON.stringify(spec.kind)} answer yet (§10.7).`);
    }
}
/**
 * Every check against one submission. `receivedAt` is the server's arrival stamp and the
 * only clock consulted; a client timestamp would be a "let me win" button.
 */
export function checkSubmission(request, actor, facts, value, receivedAt) {
    const refuse = (error) => ({ ok: false, error });
    // 6: still open.
    if (request.closed)
        return refuse('That is closed.');
    // 1: in time.
    if (request.deadlineAt !== null && receivedAt > request.deadlineAt) {
        return refuse('Time is up.');
    }
    // 2: targeted. `canDrive` is re-read from the session on every submission.
    const canDrive = actor.kind === 'display' && actor.canDrive;
    if (!targetsInclude(request.targets, actor, facts.onRoster, canDrive)) {
        return refuse('That is not for you to answer.');
    }
    // 5: one per participant.
    if (facts.existing && !request.allowRevision)
        return refuse('You have already answered.');
    // 3 + 4: the value itself.
    const spec = specFor(request, actor);
    if (!spec) {
        return refuse('There is nothing here for you to answer.');
    }
    const checked = validateValue(spec, value, participantOf(actor));
    if (!checked.ok)
        return refuse(checked.error);
    return { ok: true, value: checked.value, revision: (facts.existing?.revision ?? -1) + 1 };
}
// ---------------------------------------------------------------------------
// Reading answers back — `ctx.tally`
// ---------------------------------------------------------------------------
/**
 * Can this spec's answers be counted? `tally` returns null (not `{}`, which would read
 * as "nobody answered") for kinds that cannot. `press` is excluded: every press has the
 * same value, and `submissions()` already gives arrival order.
 */
export function tallyable(kind) {
    return (kind === 'choice' ||
        kind === 'boolean' ||
        kind === 'pickParticipant' ||
        kind === 'verdict');
}
/**
 * Counts keyed by the submitted value. A multi-pick contributes to every id it named,
 * which is why the keys are values rather than submitters.
 */
export function computeTally(submissions) {
    const counts = {};
    for (const one of submissions) {
        const keys = Array.isArray(one.value) ? one.value : [one.value];
        for (const key of keys) {
            if (typeof key !== 'string' && typeof key !== 'number' && typeof key !== 'boolean')
                continue;
            const k = String(key);
            counts[k] = (counts[k] ?? 0) + 1;
        }
    }
    return counts;
}
/**
 * Has everyone asked answered? Completion fires `onComplete`.
 * `allowRevision` never auto-completes: closing the instant the last player types would
 * take revision away. An empty target set is never complete. Deadlines are not checked
 * here; the caller arms a timer.
 */
export function isComplete(request, eligible, submitted) {
    if (request.allowRevision)
        return false;
    return eligible > 0 && submitted >= eligible;
}
//# sourceMappingURL=requests.js.map